import React from 'react';
import { ButtonV2, Flex } from '@withjoy/joykit';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useListingsPanelTelemetry } from '../ListingsPanel.telemetry';
import { withWindow } from '@shared/utils/withWindow';
import type { AccommodationListingDTO } from '@shared/types/Accommodations.types';
import { useSafeUrlParser } from '@shared/hooks/useSafeUrlParser';
import { createUtmParametersForAccommodationsConsolePage } from '@apps/stays/AccommodationsConsole/hooks/useAccommodationRedirectUrl';
import { useMergedUtmParameters } from '@shared/hooks/useMergedUtmParameters';
import { getSelectedRedirectTelemetryName } from '@shared/utils/extractHotelRedirects';
import { useTranslation } from '@shared/core';
import { HotelSearchSource } from '@shared/constants/accommodations';

type HotelCTAProps = Readonly<{
  listing: AccommodationListingDTO;
  redirectUrl?: string;
  sourceName: HotelSearchSource;
  /** Optional background override (e.g. the cash-fund variant's gold). Defaults to the primary intent. */
  backgroundColor?: string;
}>;

export const HotelCTA = ({ listing, redirectUrl, sourceName, backgroundColor }: HotelCTAProps) => {
  const telemetry = useListingsPanelTelemetry();
  const { tq } = useTranslation('accommodationsConsole');
  const parseUrl = useSafeUrlParser();
  const mergedUtm = useMergedUtmParameters(createUtmParametersForAccommodationsConsolePage(sourceName));

  const buttonTitle = sourceName === HotelSearchSource.OTA ? tq`Book on Joy` : tq`See Availability`;

  const handleClick = useEventCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    telemetry.seeAvailabilityClicked({ joyPlaceId: listing.joyPlaceId || '', hotelName: listing.name, isSponsored: listing.isSponsored ?? false, chain: listing.chain });

    if (redirectUrl) {
      const urlInfo = parseUrl(redirectUrl, 'HotelCTA.handleClick');
      if (urlInfo) {
        telemetry.hotelBookingRedirect({
          joyPlaceId: listing.joyPlaceId || '',
          hotelName: listing.name,
          destinationURL: redirectUrl,
          destinationDomain: urlInfo.hostname,
          destinationName: getSelectedRedirectTelemetryName(listing.redirectMechanisms),
          isSponsored: listing.isSponsored ?? false,
          chain: listing.chain,
          ...mergedUtm,
        });
      }
      withWindow(global => global.open(redirectUrl, '_blank'));
    }
  });

  return (
    <Flex width={{ _: '100%', md: 'fit-content' }}>
      <ButtonV2
        variant="solid"
        intent="primary"
        _hover={backgroundColor ? { backgroundColor } : { backgroundColor: { _: 'newUIBrandPrimary', md: 'buttonPrimaryHover' } }}
        backgroundColor={backgroundColor}
        height={{ _: '48px', md: '40px' }}
        width="100%"
        onClick={handleClick}
        shape="rounded"
      >
        {buttonTitle}
      </ButtonV2>
    </Flex>
  );
};
